import React from "react";
import { useI18n } from "@/contexts/I18nContext";
import LegalPage from "./LegalPage";
import { BUSINESS } from "@/lib/business";

const CONTENT = {
  ar: {
    eyebrow: "قانوني",
    title: "سياسة الاسترداد",
    updatedAt: "٢٤ فبراير ٢٠٢٦",
    intro:
      "DAAM خدمة رقمية تُستهلك مباشرة بعد الدفع (توليد صور، فيديو، ومحادثات ذكاء اصطناعي). لهذا السبب لا نُصدر استردادات تلقائية. تُدرَس كل طلبات الاسترداد يدوياً وفق الفئات أدناه.",
    sections: [
      { title: "١. متى قد يكون الاسترداد ممكناً",
        paragraphs: ["نُقيّم كل طلب على حدة، مع تفضيل واضح للحالات التالية:"],
        list: [
          "خصم مكرّر: خصم نفس الاشتراك مرتين أو أكثر خلال نفس الدورة نتيجة خطأ تقني.",
          "عطل تقني مؤكّد يمنع الوصول إلى الخدمة لفترة طويلة رغم بلاغك للدعم ومحاولات فريقنا الحلّ.",
          "فترة اشتراك مدفوعة ولم تُستخدم إطلاقاً: أي دورة فوترة جديدة تم تجديدها آلياً بعد نسيانك للإلغاء، بشرط ألّا تكون أنشأت أي محتوى مدفوع خلالها.",
        ]},
      { title: "٢. متى لا نُصدر استرداداً",
        list: [
          "حصص استخدام قد تم استهلاكها فعلياً (صور مُولّدة، فيديو، أو رسائل محادثة).",
          "الفترة التجريبية بعد بدايتها – حتى في حال الإلغاء لاحقاً.",
          "رفض بطاقتك المصرفية أو رفض عملية الشراء من قِبَل مصرفك (يُرجى مراجعة مصرفك).",
          "طلبات مقدَّمة بعد أكثر من ٣٠ يوماً من تاريخ الخصم.",
          "إساءة استخدام الحساب أو مخالفة شروط الخدمة.",
          "التغيير الاختياري إلى خطة أدنى قبل نهاية الدورة (الاشتراك الجديد يبدأ من الدورة التالية).",
        ]},
      { title: "٣. كيفية طلب الاسترداد",
        paragraphs: [
          "أرسل طلبك عبر نموذج الدعم في صفحة \"تواصل معنا\" واختر فئة \"استرداد\". أرفق:",
        ],
        list: [
          "البريد الإلكتروني المسجَّل به الحساب.",
          "الرقم المرجعي للاشتراك أو تاريخ الخصم.",
          "شرح موجَز للسبب.",
          "أي دليل تقني (لقطة شاشة، رقم مرجعي للخطأ من رسالة الدعم السابقة).",
        ]},
      { title: "٤. الجدول الزمني للمراجعة",
        paragraphs: [
          "نلتزم بالرد على طلبات الاسترداد خلال ٣ أيام عمل. في حال الموافقة يتم تحويل المبلغ إلى بطاقتك عبر Tap Payments وقد يستغرق البنك ٥ إلى ١٤ يوم عمل لإظهاره في كشف حسابك.",
          "قرار الاسترداد يخضع لتقدير فريق DAAM بعد مراجعة السجلات التقنية والاستخدام الفعلي، ولا يُعتبر ضمناً في الشروط.",
        ]},
      { title: "٥. الحالات الخاصة",
        paragraphs: [
          "في حال إغلاق DAAM لخدمة مدفوعة كنت مشتركاً بها بشكل نهائي ودون إشعار مسبق كافٍ، سنقوم برد المبلغ المتناسب للفترة غير المستخدمة.",
          "إغلاق حسابك من طرفنا بسبب انتهاك الشروط لا يمنحك حق الاسترداد.",
        ]},
      { title: "٦. التواصل معنا",
        paragraphs: [`لأي استفسار حول الاسترداد: ${BUSINESS.email_support}`]},
    ],
  },
  en: {
    eyebrow: "Legal",
    title: "Refund Policy",
    updatedAt: "February 24, 2026",
    intro:
      "DAAM is a digital service consumed immediately after payment (AI-generated images, videos and chat). For this reason we do not issue automatic refunds. Every refund request is reviewed individually against the categories below.",
    sections: [
      { title: "1. When a refund may be possible",
        paragraphs: ["Each request is evaluated on its own merits, with a clear preference for the following cases:"],
        list: [
          "Duplicate charge: the same subscription is charged twice or more within the same billing cycle due to a technical error.",
          "Confirmed technical failure preventing access to the service for a prolonged period despite your report to support and our team's remediation attempts.",
          "Paid billing period that was never used: a renewal cycle that auto-renewed after you forgot to cancel, provided no paid content was generated during it.",
        ]},
      { title: "2. When we do not refund",
        list: [
          "Usage that has already been consumed (generated images, video seconds, chat messages).",
          "The free trial once it has started — even if cancelled later.",
          "Card refusal or purchase denial by your bank (please contact your bank).",
          "Requests filed more than 30 days after the charge date.",
          "Account misuse or breach of the Terms of Service.",
          "Voluntary downgrade to a lower plan before the end of the current cycle (the new plan starts at the next cycle).",
        ]},
      { title: "3. How to request a refund",
        paragraphs: ["Submit your request via the support form on the \"Contact us\" page and choose the \"Refund\" category. Please include:"],
        list: [
          "The email address registered on the account.",
          "The subscription reference number or the charge date.",
          "A brief explanation of the reason.",
          "Any technical evidence (screenshot, reference number from a prior support reply).",
        ]},
      { title: "4. Review timeline",
        paragraphs: [
          "We aim to respond to refund requests within 3 business days. When approved, the funds are returned to your card via Tap Payments; your bank may take 5-14 business days to show the credit on your statement.",
          "The refund decision remains at DAAM's discretion after reviewing technical logs and actual usage, and is not guaranteed by these terms.",
        ]},
      { title: "5. Special cases",
        paragraphs: [
          "If DAAM permanently discontinues a paid service you are subscribed to without sufficient prior notice, we will refund the pro-rata unused portion.",
          "Termination by DAAM due to a breach of these terms does not entitle you to a refund.",
        ]},
      { title: "6. Contact us",
        paragraphs: [`For any refund enquiry: ${BUSINESS.email_support}`]},
    ],
  },
};

export default function RefundPage() {
  const { lang } = useI18n();
  const c = CONTENT[lang] || CONTENT.en;
  return (
    <LegalPage testId="refund-page" eyebrow={c.eyebrow} title={c.title}
      updatedAt={c.updatedAt} intro={c.intro} sections={c.sections} />
  );
}
